import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import fetchCurrentUserAction from '@store/actions/currentUser';
import { StoreState, IterableUserAttributes, CurrentUser } from '@root/types';

import { setBugsnagUser } from '@shared/bugsnagClient';
import useEnv from '@shared/useEnv';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { parse } from 'qs';
import routes from '@root/routes';
import {
  receiveCurrentUser,
  receiveNoUser,
} from '@root/store/actions/currentUser/fetchCurrentUser';
import { useQuery } from '@tanstack/react-query';
import { get } from '@shared/typedApi';
import { currentUserKey } from '@shared/useCurrentUser';

// This file is mostly about loading and setting the current user
// in redux and react-query (remove line w/ demand.current-user-react-query)
// to actually use the current user, see the useCurrentUser hook
// or (and remove after demand.current-user-react-query), the redux store

export const userShapedForLaunchDarkly = (user: CurrentUser | null) =>
  user
    ? {
        key: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        custom: {
          market: user.marketName,
          role: user.role,
        },
      }
    : { anonymous: true };

const useUserRegisterer = (onRegister: () => void = () => {}) => {
  const flags = useFlags();
  const useReactQuery = flags['demand.current-user-react-query'];

  const currentUser = useSelector((state: StoreState) => state.currentUser);
  const dispatch = useDispatch();
  const fetchCurrentUser = (iterableUserAttributes: IterableUserAttributes) =>
    dispatch(fetchCurrentUserAction(iterableUserAttributes));
  const client = useLDClient();
  const { railsEnv } = useEnv();

  const ITERABLE_COOKIES = [
    'iterableEndUserId',
    'iterableEmailCampaignId',
    'iterableTemplateId',
    'iterableMessageId',
  ];
  const [cookies] = useCookies([...ITERABLE_COOKIES]);
  const { iterableEmailCampaignId, iterableTemplateId, iterableMessageId, iterableEndUserId } =
    cookies;

  const { search: urlParams } = useLocation();

  const {
    email,
    templateId: emailIterableTemplateId,
    campaignId: emailIterableCampaignId,
  } = parse(urlParams, { ignoreQueryPrefix: true });

  const decodedEmail = email ? atob(email as string) : undefined;
  const iterableUserAttributes: IterableUserAttributes = {
    iterableEndUserId: iterableEndUserId ?? decodedEmail,
    iterableEmailCampaignId: iterableEmailCampaignId ?? emailIterableCampaignId,
    iterableTemplateId: iterableTemplateId ?? emailIterableTemplateId,
    iterableMessageId,
  };

  // remove with demand.current-user-react-query
  useEffect(() => {
    if (useReactQuery) return;
    fetchCurrentUser(iterableUserAttributes);
    // FIXME: Either add the exhaustive deps or delete this line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCurrentUserAction]);

  useEffect(() => {
    if (railsEnv === 'test') return;
    if (useReactQuery) return;

    client?.identify(userShapedForLaunchDarkly(currentUser), undefined, onRegister);
    setBugsnagUser(currentUser);
    // FIXME: Either add the exhaustive deps or delete this line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, client]);

  useQuery(currentUserKey, () => get<CurrentUser>(routes.api.currentUser), {
    retry: false,
    onSuccess: data => {
      dispatch(receiveCurrentUser(data, iterableUserAttributes));

      if (railsEnv !== 'test') {
        client?.identify(userShapedForLaunchDarkly(data), undefined, onRegister);
        setBugsnagUser(data);
      }
    },
    onError: () => {
      dispatch(receiveNoUser(iterableUserAttributes));
      if (railsEnv === 'test') return;
      client?.identify({ anonymous: true }, undefined, onRegister);
    },
    enabled: useReactQuery && railsEnv !== 'test',
  });

  return null;
};
export default useUserRegisterer;
